import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MACHINES, LEASE_MONTHS, fmt } from "@/lib/pricing";
import { http, inputCls } from "@/lib/admin";
import { invalidatePricing } from "@/hooks/usePricing";
import { Save } from "lucide-react";

const COMPANY_FIELDS = [
  ["name", "Cégnév"], ["address", "Cím"], ["tax_id", "Adószám"], ["email", "E-mail"],
  ["phone", "Telefon"], ["website", "Weboldal"], ["bank_account", "Bankszámlaszám"],
];

export function PricingPanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/pricing").then((r) => setForm({ ...r.data.input, company: r.data.company }))
      .catch(() => toast.error("Nem sikerült betölteni az árakat"));
  }, []);

  if (!form) return <p className="text-[var(--ink-muted)]">Betöltés...</p>;

  const setMachine = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));
  const setLease = (key, lease, patch) =>
    setMachine(key, { [lease]: { ...form[key][lease], ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/pricing", form);
      invalidatePricing();
      toast.success("Alapárak mentve – a kalkulátor és az árajánlatok már az új árakkal számolnak");
    } catch {
      toast.error("Sikertelen mentés");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="pricing-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow text-[var(--terra)]">Alapárak</p>
          <h2 className="font-serif-d font-semibold text-2xl mt-1">Árazás beállítása</h2>
          <p className="text-sm text-[var(--ink-soft)] mt-1.5 max-w-xl">
            Az itt megadott nettó árak azonnal érvényesek a weboldal kalkulátorában és az árajánlatkészítőben.
            A futamidő végösszege automatikusan számolódik: kezdő befizetés + havi részlet × hónapok.
          </p>
        </div>
        <button data-testid="pricing-save-btn" onClick={save} disabled={saving}
          className="btn-terra rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Mentés..." : "Árak mentése"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {Object.entries(MACHINES).map(([key, m]) => (
          <div key={key} data-testid={`pricing-card-${key}`} className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <h3 className="font-serif-d font-semibold text-xl">{m.label}</h3>
            <p className="text-xs text-[var(--ink-muted)]">{m.sub}</p>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Field label="Egyösszegű ár (nettó)" testId={`pricing-${key}-price`} value={form[key].price}
                onChange={(v) => setMachine(key, { price: v })} />
              <Field label="Expressz díj / db (48 nap)" testId={`pricing-${key}-express`} value={form[key].express_fee}
                onChange={(v) => setMachine(key, { express_fee: v })} />
            </div>

            <table className="w-full mt-5 text-sm">
              <thead>
                <tr className="eyebrow text-[var(--ink-muted)] text-left">
                  <th className="pb-2 font-normal">Futamidő</th>
                  <th className="pb-2 font-normal">Kezdő befizetés</th>
                  <th className="pb-2 font-normal">Havi részlet</th>
                  <th className="pb-2 font-normal text-right">Végösszeg</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(LEASE_MONTHS).map(([lease, months]) => {
                  const { down, monthly } = form[key][lease];
                  return (
                    <tr key={lease} className="border-t border-[var(--line)]">
                      <td className="py-2.5 font-semibold">{months} hó</td>
                      <td className="py-2.5 pr-2">
                        <NumInput testId={`pricing-${key}-${lease}-down`} value={down} onChange={(v) => setLease(key, lease, { down: v })} />
                      </td>
                      <td className="py-2.5 pr-2">
                        <NumInput testId={`pricing-${key}-${lease}-monthly`} value={monthly} onChange={(v) => setLease(key, lease, { monthly: v })} />
                      </td>
                      <td className="py-2.5 text-right font-serif-d font-semibold text-[var(--terra)] whitespace-nowrap" data-testid={`pricing-${key}-${lease}-total`}>
                        {fmt(down + monthly * months)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mt-6" data-testid="company-card">
        <h3 className="font-serif-d font-semibold text-xl">Cégadatok az árajánlaton</h3>
        <p className="text-xs text-[var(--ink-muted)]">Ezek az adatok jelennek meg a PDF árajánlat fejlécében és láblécében.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {COMPANY_FIELDS.map(([k, label]) => (
            <label key={k} className="block">
              <span className="eyebrow text-[var(--ink-muted)] block mb-1.5">{label}</span>
              <input data-testid={`company-${k}`} value={form.company[k] || ""} className={inputCls}
                onChange={(e) => setForm((f) => ({ ...f, company: { ...f.company, [k]: e.target.value } }))} />
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

const NumInput = ({ value, onChange, testId }) => (
  <input type="number" min={0} step={1000} data-testid={testId} value={value}
    onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
    className={`${inputCls} font-mono-d`} />
);

const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="eyebrow text-[var(--ink-muted)] block mb-1.5">{label}</span>
    <NumInput value={value} onChange={onChange} testId={testId} />
  </label>
);
